import React, { useState, useEffect } from 'react';
import { Shield, CheckCircle, Star, TrendingUp, Award } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { fetchGeolocation, submitLead, getCountryDialCodes, preparLeadData } from '../services/api';

export const LandingPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    countryCode: '+1',
    phone: '',
    message: ''
  });
  const [geoData, setGeoData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const countryCodes = getCountryDialCodes();

  useEffect(() => {
    const loadGeolocation = async () => {
      const data = await fetchGeolocation();
      if (data && data.isSuccess) {
        setGeoData(data);
        const matchingCountry = countryCodes.find(c => c.countryCode === data.data.countryCode);
        if (matchingCountry) {
          setFormData(prev => ({ ...prev, countryCode: matchingCountry.code }));
        }
      }
    };
    
    loadGeolocation();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      const selectedCountry = countryCodes.find(c => c.code === formData.countryCode);
      
      const leadData = preparLeadData(formData, geoData, selectedCountry);

      console.log('Submitting lead data:', leadData);
      
      const result = await submitLead(leadData);
      
      console.log('API Result:', result);

      if (result.success && result.statusCode === 200 && result.customerId) {
        // Full page redirect (external URL)
        window.location.href = `/application/main?customerid=${result.customerId}`;
      } else {
        // Show the actual API error message
        const errorMsg = result.message || result.data?.message || 'An error occurred. Please try again.';
        console.error('Form submission failed:', errorMsg);
        setError(errorMsg);
      }
    } catch (err) {
      console.error('Form submission error:', err);
      setError('Network error. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section with Form */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-red-600 via-red-700 to-red-800">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Side - Content */}
            <div className="text-white space-y-6">
              <div className="inline-flex items-center space-x-2 bg-yellow-400 text-gray-900 px-4 py-2 rounded-full font-semibold">
                <Shield className="w-5 h-5" />
                <span>Trusted by 10,000+ Businesses</span>
              </div>
              <h1 className="text-5xl md:text-6xl font-bold leading-tight">
                Protect Your Brand Today
              </h1>
              <p className="text-xl text-white/90 leading-relaxed">
                Professional trademark registration services starting at just $45. Fast, affordable, and hassle-free protection for your business.
              </p>
              <ul className="space-y-3">
                {[
                  '98% Success Rate',
                  'Filed within 24-48 Hours',
                  'Expert Trademark Attorneys',
                  'Comprehensive Search Included',
                  'Ongoing Monitoring & Support'
                ].map((item, index) => (
                  <li key={index} className="flex items-center space-x-3">
                    <CheckCircle className="w-6 h-6 text-yellow-400 flex-shrink-0" />
                    <span className="text-lg">{item}</span>
                  </li>
                ))}
              </ul>
              <div className="flex items-center space-x-8 pt-4">
                <div>
                  <div className="text-4xl font-bold text-yellow-400">10,000+</div>
                  <div className="text-white/80">Trademarks Filed</div>
                </div>
                <div>
                  <div className="text-4xl font-bold text-yellow-400">98%</div>
                  <div className="text-white/80">Success Rate</div>
                </div>
              </div>
            </div>

            {/* Right Side - Form */}
            <div className="bg-white rounded-2xl shadow-2xl p-8">
              <div className="text-center mb-6">
                <h2 className="text-3xl font-bold text-gray-900 mb-2">
                  Get Started Today
                </h2>
                <p className="text-gray-600">
                  Fill out the form and our experts will contact you within 2 hours
                </p>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="lp-name">Full Name *</Label>
                  <Input
                    id="lp-name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="John Doe"
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="lp-email">Email Address *</Label>
                  <Input
                    id="lp-email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="john@example.com"
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label>Contact Number *</Label>
                  <div className="flex space-x-2 mt-1">
                    <Select
                      value={formData.countryCode}
                      onValueChange={(value) => setFormData({ ...formData, countryCode: value })}
                    >
                      <SelectTrigger className="w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {countryCodes.map((item) => (
                          <SelectItem key={item.code} value={item.code}>
                            {item.code} {item.country}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      type="tel"
                      required
                      value={formData.phone}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '');
                        if (value.length <= 10) {
                          setFormData({ ...formData, phone: value });
                        }
                      }}
                      placeholder="1234567890"
                      maxLength={10}
                      pattern="[0-9]{10}"
                      className="flex-1"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="lp-message">Tell Us About Your Trademark</Label>
                  <Textarea
                    id="lp-message"
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    placeholder="What would you like to trademark?"
                    rows={4}
                    className="mt-1"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-6 text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Submitting...' : 'Start Your Application Now'}
                </Button>

                <p className="text-xs text-gray-500 text-center">
                  By submitting, you agree to our Terms of Service and Privacy Policy
                </p>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8 text-center">
            {[
              { icon: Shield, value: '10,000+', label: 'Trademarks Protected' },
              { icon: Star, value: '4.9/5', label: 'Client Satisfaction' },
              { icon: TrendingUp, value: '98%', label: 'Approval Rate' },
              { icon: Award, value: '24/7', label: 'Expert Support' }
            ].map((stat, index) => {
              const Icon = stat.icon;
              return (
                <div key={index}>
                  <div className="bg-red-100 w-16 h-16 rounded-full flex items-center justify-center mb-4 mx-auto">
                    <Icon className="w-8 h-8 text-red-600" />
                  </div>
                  <div className="text-4xl font-bold text-gray-900 mb-2">{stat.value}</div>
                  <div className="text-gray-600">{stat.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Why Choose <span className="text-red-600">Global Mark Registry?</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We make trademark protection simple, fast, and affordable
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {[
              {
                title: 'Expert Guidance',
                description: 'Experienced trademark attorneys handle your application from start to finish, ensuring the highest chance of approval.'
              },
              {
                title: 'Fast Processing',
                description: 'Your application is typically filed with the USPTO within 24-48 hours of receiving your information.'
              },
              {
                title: 'Transparent Pricing',
                description: 'No hidden fees or surprises. Our all-inclusive packages cover everything you need for trademark protection.'
              },
              {
                title: 'Comprehensive Search',
                description: 'Detailed trademark search identifies potential conflicts before filing, reducing rejection risk significantly.'
              },
              {
                title: 'Ongoing Monitoring',
                description: 'Continuous monitoring protects your trademark from infringement and unauthorized use after registration.'
              },
              {
                title: 'Dedicated Support',
                description: 'Your personal case manager is available to answer questions and provide updates throughout the process.'
              }
            ].map((benefit, index) => (
              <div key={index} className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all">
                <h3 className="text-xl font-bold text-gray-900 mb-3">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Trusted by <span className="text-red-600">Thousands</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {[
              {
                name: 'Sarah Mitchell',
                role: 'CEO, TechStart Solutions',
                text: 'Global Mark Registry made the process incredibly smooth. Our trademark was approved faster than expected!'
              },
              {
                name: 'Michael Chen',
                role: 'Founder, Artisan Coffee Co.',
                text: 'Professional service from start to finish. The team was always available to answer my questions.'
              },
              {
                name: 'Emily Watson',
                role: 'Owner, EcoWear Fashion',
                text: 'Best decision for our brand! The comprehensive search gave us complete peace of mind.'
              }
            ].map((testimonial, index) => (
              <div key={index} className="bg-gray-50 rounded-xl p-6">
                <div className="flex space-x-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-gray-700 mb-4 italic">"{testimonial.text}"</p>
                <div className="border-t pt-4">
                  <div className="font-bold text-gray-900">{testimonial.name}</div>
                  <div className="text-sm text-gray-600">{testimonial.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-r from-red-600 to-red-700">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Don't Wait – Protect Your Brand Today!
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Every day without trademark protection is a day your brand is at risk. Get started now with our expert team.
          </p>
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            className="inline-block bg-yellow-400 hover:bg-yellow-500 text-gray-900 px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300 hover:shadow-xl"
          >
            Fill Out The Form Above
          </a>
        </div>
      </section>
    </div>
  );
};